import type { CommercialWorkspaceData } from "./types";

export interface CreateSupplierRequest {
  displayName: string;
  website?: string;
  domain?: string;
  countryCode?: string;
  defaultCurrency?: string;
  legalName?: string;
  tradingName?: string;
  regionCode?: string;
  supplierType?: string;
  riskLevel?: string;
  paymentTerms?: string;
  leadTimeProfile?: string;
  qualityStatus?: string;
  complianceStatus?: string;
}

export interface UpdateSupplierRequest {
  displayName: string;
  website?: string | null;
  domain?: string | null;
  countryCode?: string | null;
  defaultCurrency?: string;
  supplierType?: string;
  riskLevel?: string;
  leadTimeProfile?: string;
  qualityStatus?: string;
  complianceStatus?: string;
}

const baseUrl = import.meta.env.VITE_CRM_API_BASE_URL ?? "/api/v1";

export class CommercialApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    readonly detail?: string,
  ) {
    super(detail ?? `Commercial request failed (${status})`);
    this.name = "CommercialApiError";
  }
}

export async function loadCommercialWorkspace(): Promise<CommercialWorkspaceData> {
  const response = await fetch(`${baseUrl}/commercial-workspace`, {
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!response.ok) throw await apiError(response);
  return response.json() as Promise<CommercialWorkspaceData>;
}

export async function createSupplier(
  input: CreateSupplierRequest,
): Promise<void> {
  const csrf = readCsrf();
  const response = await fetch(`${baseUrl}/suppliers`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": `commercial-supplier-${crypto.randomUUID()}`,
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await apiError(response);
}

export async function updateSupplier(
  supplierIdentityId: string,
  input: UpdateSupplierRequest,
): Promise<void> {
  const csrf = readCsrf();
  const response = await fetch(`${baseUrl}/suppliers/${supplierIdentityId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
    },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await apiError(response);
}

export async function syntheticSend(path: string): Promise<void> {
  const csrf = readCsrf();
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": `commercial-fe-${crypto.randomUUID()}`,
    },
    body: JSON.stringify({ testOnly: true }),
  });
  if (!response.ok) throw await apiError(response);
}

export async function decideApproval(
  approvalId: string,
  expectedVersion: number,
  action: "APPROVE" | "REJECT" | "REQUEST_CHANGES",
  reason: string,
): Promise<void> {
  const csrf = readCsrf();
  const response = await fetch(`${baseUrl}/approvals/${approvalId}/decisions`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-csrf-token": csrf,
      "x-idempotency-key": `commercial-fe-${crypto.randomUUID()}`,
    },
    body: JSON.stringify({ action, expectedVersion, reason }),
  });
  if (!response.ok) throw await apiError(response);
}

function readCsrf(): string {
  const csrf = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  if (!csrf) throw new CommercialApiError(401, "SESSION_EXPIRED");
  return decodeURIComponent(csrf);
}

async function apiError(response: Response): Promise<CommercialApiError> {
  let code: string | undefined;
  let detail: string | undefined;
  try {
    const payload = (await response.json()) as {
      code?: unknown;
      message?: unknown;
    };
    if (typeof payload.code === "string") code = payload.code;
    if (typeof payload.message === "string") detail = payload.message;
  } catch {
    // Stable status category only.
  }
  return new CommercialApiError(response.status, code, detail);
}
